// Source individuelle d'un prix unitaire issu de la base de données
export interface PrixUnitaireSourceItem {
  id: number;
  nom: string;
  localisation: string;
  annee: string;
  fournisseur: string;
  prixUnitaire: number; // valeur numérique parsée
  active: boolean;      // false = exclu du calcul par l'utilisateur
}

// Types simplifiés pour les blocs
export interface Block {
  id: string;
  parentId?: string;
  order: number;
  level: number;
  /** Explicit type: 'titre' = section/lot (no quantitative data), 'prestation' = line with quantities */
  blockType?: 'titre' | 'prestation';
  isCollapsed?: boolean;
  children: string[];
  content: BlockContent;
  /** Budget initial du lot (level 0). Sert de baseline éditable pour comparer avec le total courant. */
  budgetInitial?: number;
}

export type PrixReliability = 'high' | 'medium' | 'low';
export type PrixSource = 'database' | 'user';

export interface BlockContent {
  titre: string;
  // Données de chiffrage (optionnelles)
  intitule?: string;
  categorie?: string;
  unite?: string;
  prixUnitaire?: string;
  quantite?: string;
  prixTotal?: string;
  chantier?: string;
  fournisseur?: string;
  // Traçabilité du prix unitaire
  prixUnitaireReliability?: PrixReliability; // fiabilité issue de la base de données
  prixUnitaireSource?: PrixSource;           // 'database' = prix DB, 'user' = modifié manuellement
  prixUnitaireOriginal?: string;             // prix DB conservé après modification utilisateur
  prixUnitaireMatchType?: 'prestation' | 'ensemble'; // type de la source DB ('ensemble' inclut lots)
  prixUnitaireExpertCurated?: boolean;       // true = PU issu d'une sélection manuelle via la modale
  // Total manuel
  prixTotalManual?: boolean;                            // true si prixTotal a été saisi directement par l'utilisateur
  // Sources détaillées du prix unitaire (issues de la base de données)
  prixUnitaireSources?: PrixUnitaireSourceItem[];       // chantiers ayant servi à calculer le PU moyen
  // Ventilation
  matchedItemId?: string;                               // id de l'élément associé (prestation/lot/ensemble)
  matchedItemType?: 'prestation' | 'lot' | 'ensemble'; // type de l'élément associé
  isVentilationCostLine?: boolean;                      // distingue les lignes auto-générées des lignes originales
  // Décomposition Fourniture / Main d'œuvre (optionnelle, par ligne)
  decompositionMode?: DecompositionMode;                // absent = 'forfaitaire' (legacy)
  coutFourniture?: number;                              // €/unité — 'fourniture-seule' et 'fourni-pose'
  tempsPose?: number;                                   // h/unité — 'fourni-pose'
  nbHommes?: number;                                    // taille d'équipe — 'fourni-pose'
  tempsAppro?: number;                                  // h/ligne — override du défaut global, 'fourni-pose'
  decompositionSource?: DecompositionSource;            // origine des valeurs F/MO (pré-remplies DB vs saisies user)
  // Fourniture : intitulé + traçabilité PU (match DB sur la fourniture)
  fournitureIntitule?: string;                          // libellé de la fourniture (défaut = intitulé de la prestation)
  fournitureSource?: PrixSource;                        // 'database' = fourniture matchée en base, 'user' = saisie manuelle
  fournitureReliability?: PrixReliability;              // fiabilité du PU fourniture quand il vient de la base
  fournitureSources?: PrixUnitaireSourceItem[];         // chantiers ayant servi à calculer le PU fourniture moyen
  // Multiplicateur appliqué au PU de la ligne (saisi ou dérivé de la décomposition)
  // pour obtenir le total : Total = PU × coefLigne × qté. Undefined = défaut
  // global (params.coefDefaut). Externe à la décomposition.
  coefLigne?: number;
}

export type DecompositionMode = 'forfaitaire' | 'fourniture-seule' | 'fourni-pose';
export type DecompositionSource = 'database' | 'user';

// Vue globale du chiffrage :
// - 'client'    : ne montre que les lignes destinées au client (cache ventilation + décomposition F/MO).
// - 'ventilee'  : montre les lignes de ventilation inline, MAIS pas les subbars de décomposition F/MO.
// - 'detaillee' : montre tout — lignes ventilées + subbars de décomposition F/MO.
// Les trois vues ne modifient PAS les données : les montants PU/Total persistent
// identiquement, seul l'affichage du détail interne change.
export type ChiffrageView = 'client' | 'ventilee' | 'detaillee';

// Fonctions utilitaires pour déterminer le comportement
// blockType est l'autorité explicite ; fallback sur le contenu pour les anciens blocs.
export const isPrestation = (block: Block): boolean => {
  if (block.blockType === 'titre') return false;
  if (block.blockType === 'prestation') return true;
  // Fallback : inférer depuis le contenu (rétrocompatibilité)
  return !!(block.content.prixUnitaire || block.content.quantite || block.content.unite || block.content.intitule);
};

export const isSection = (block: Block): boolean => {
  return !isPrestation(block);
};

// État de l'éditeur
export interface BlockEditorState {
  blocks: Record<string, Block>;
  rootBlocks: string[];
  selectedBlockId?: string;
  editingBlockId?: string;
}

// Actions sur les blocs
export interface BlockActions {
  addBlock: (parentId?: string, afterBlockId?: string) => string;
  addPrestation: (parentId?: string, afterBlockId?: string) => string;
  addSection: (parentId?: string, afterBlockId?: string) => string;
  updateBlock: (id: string, updates: Partial<Block>) => void;
  deleteBlock: (id: string) => void;
  moveBlock: (id: string, newParentId?: string, newOrder?: number) => void;
  indentBlock: (id: string) => void;
  unindentBlock: (id: string) => void;
  clearValues: (id: string) => void;
  addValues: (id: string) => void;
  syncLevels: () => void;
}

// Props pour les composants de blocs
export interface BlockComponentProps {
  block: Block;
  isSelected: boolean;
  isEditing: boolean;
  isDragging?: boolean;
  onSelect: (id: string) => void;
  onEdit: (id: string) => void;
  onUpdate: (id: string, updates: Partial<Block>) => void;
  onDelete: (id: string) => void;
  onDragStart?: (id: string, event: React.MouseEvent<Element>) => void;
  onDragEnd?: () => void;
  onStopEditing: () => void;
}
